// ── Novelty Inspector ──────────────────────────────────────
// Replaces the stacked Novelty / DMAD / QD / Recombinations sections
// in GenerationFlowCard with a tabbed container.
//
// Tabs:
//   📊 Scores   → top-novel ideas + average + filter shortcuts
//   🗺 Map      → QD archive grid (interactive — clicks set filters)
//   🧬 Blends   → recombinations as cards, sorted by novelty
//   🧠 Lenses   → 6 reasoners with their critiques + transformations
//
// One section visible at a time → less cognitive load, more breathing room.

import React, { useState } from 'react';
import { useNovelty, noveltyColor, noveltyLabel } from './NoveltyContext';
import EvolutionTimeline from './EvolutionTimeline';
import { CompetitionTab } from './CompetitionUI';

// Map a live ProgressStrip stage → the inspector tab it populates. Used to
// (a) flash a "live" pill on the streaming tab and (b) auto-jump to a tab
// the moment its first row of data arrives mid-generation.
const STAGE_TO_TAB = {
  dmad: 'lenses',
  score: 'scores',
  tag: 'map',
  blend: 'blends',
  blend_score: 'blends',
};

export default function NoveltyInspector({ noveltySummary }) {
  const { scores, archive, recombinations, dmadResults, dmadStatus, progress, evolveGenerations, competition } = useNovelty();
  const [activeTab, setActiveTab] = useState(null); // initialized once we know what's available
  const [userPickedTab, setUserPickedTab] = useState(false);
  const scoredCount = Object.keys(scores).length;
  const evolveGenCount = Object.keys(evolveGenerations || {}).length;
  const evolveCandidateCount = Object.values(evolveGenerations || {}).reduce((acc, arr) => acc + (arr?.length || 0), 0);
  const competitionCount = Object.keys(competition || {}).length;
  const isStreaming = !!progress?.activeStage;
  const hasAnyData = scoredCount > 0 || dmadResults.length > 0 || archive || recombinations.length > 0 || isStreaming || evolveGenCount > 0 || competitionCount > 0;

  // All tabs always shown during/after a run so users can see which surfaces
  // are still incoming. Disabled visually when count=0 + not the live stage.
  const liveTabId = STAGE_TO_TAB[progress?.activeStage] || null;
  const allTabs = [
    { id: 'scores',  label: 'SCORES',  icon: '📊', count: scoredCount },
    { id: 'map',     label: 'MAP',     icon: '🗺', count: archive?.stats?.filledCells || 0 },
    { id: 'blends',  label: 'BLENDS',  icon: '🧬', count: recombinations.length },
    { id: 'lenses',  label: 'LENSES',  icon: '🧠', count: dmadResults.length },
    { id: 'evolve',  label: 'EVOLVE',  icon: '🌱', count: evolveCandidateCount },
    { id: 'competition', label: 'COMPETITION', icon: '🌐', count: competitionCount },
  ];
  const tabs = allTabs.map(t => ({
    ...t,
    hasData: t.count > 0,
    live: t.id === liveTabId,
    visible: t.count > 0 || t.id === liveTabId,
  })).filter(t => t.visible);

  // Sync activeTab with first available tab when data first arrives
  React.useEffect(() => {
    if (!activeTab && tabs.length > 0) setActiveTab(tabs[0].id);
  }, [activeTab, tabs]);

  // Auto-jump to the live tab when streaming and the user hasn't chosen
  // their own tab yet. Lets users watch data land in real time without
  // hunting for it; once they click a tab, we respect that choice.
  React.useEffect(() => {
    if (!userPickedTab && liveTabId && liveTabId !== activeTab) {
      setActiveTab(liveTabId);
    }
  }, [liveTabId, userPickedTab, activeTab]);

  // Reset the "user picked" lock on the rising edge of a new generation
  // so auto-jump re-engages for run #2 in the same session. We treat
  // activeStage transitioning from null → truthy as a fresh run.
  const prevActiveStageRef = React.useRef(progress?.activeStage || null);
  React.useEffect(() => {
    const prev = prevActiveStageRef.current;
    const curr = progress?.activeStage || null;
    if (!prev && curr) setUserPickedTab(false);
    prevActiveStageRef.current = curr;
  }, [progress?.activeStage]);

  if (!hasAnyData) return null;
  const currentTab = activeTab || tabs[0]?.id;

  return (
    <div className="novelty-inspector" style={{
      marginTop: 12,
      paddingTop: 12,
      borderTop: '1px solid #2a2a2a',
    }}>
      {/* Tab header — sticks to top while scrolling so the inspector stays
          navigable during long streaming runs. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: 'var(--bg-card, #0e0e16)',
          paddingBottom: 8,
          borderBottom: '1px solid #1f1f1f',
          marginBottom: 10,
        }}
      >
        {isStreaming && (
          <div
            role="status"
            aria-live="polite"
            style={{
              fontSize: 10,
              color: '#a78bfa',
              fontFamily: 'var(--font-mono)',
              letterSpacing: 1,
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span className="refine-pulse" aria-hidden="true">●</span> STREAMING
          </div>
        )}
        <div
          role="tablist"
          aria-label="Novelty inspector tabs"
          style={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
        {tabs.map(t => {
          const active = t.id === currentTab;
          const empty = !t.hasData && !t.live;
          return (
            <button
              key={t.id}
              id={`novelty-tab-${t.id}`}
              role="tab"
              aria-selected={active}
              aria-controls="novelty-tabpanel"
              aria-label={`${t.label}${t.live ? ' (streaming)' : ''}${t.count ? `: ${t.count}` : ''}`}
              onClick={() => { setUserPickedTab(true); setActiveTab(t.id); }}
              disabled={empty}
              style={{
                background: active ? '#2a1f4a' : 'transparent',
                color: active ? '#fff' : empty ? '#444' : '#777',
                border: t.live && !active ? '1px solid #a78bfa55' : '1px solid transparent',
                padding: '6px 10px',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                fontWeight: active ? 700 : 500,
                cursor: empty ? 'default' : 'pointer',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: empty ? 0.5 : 1,
                transition: 'background 0.15s ease, color 0.15s ease',
              }}
            >
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {t.count > 0 && (
                <span style={{ color: active ? '#a78bfa' : '#555', fontWeight: 600 }}>{t.count}</span>
              )}
              {t.live && (
                <span
                  className="refine-pulse"
                  aria-hidden="true"
                  title="Streaming data into this tab"
                  style={{ color: '#a78bfa', fontSize: 10, marginLeft: 2 }}
                >●</span>
              )}
            </button>
          );
        })}
        </div>
      </div>

      {/* Live progress strip */}
      <ProgressStrip progress={progress} dmadStatus={dmadStatus} />

      {/* Active tab content */}
      <div
        id="novelty-tabpanel"
        role="tabpanel"
        aria-labelledby={`novelty-tab-${currentTab}`}
        className="novelty-inspector-body"
      >
        {currentTab === 'scores' && (scoredCount > 0 ? <ScoresTab noveltySummary={noveltySummary} /> : <StreamingPlaceholder label="scoring nodes" />)}
        {currentTab === 'map' && (archive ? <MapTab /> : <StreamingPlaceholder label="building QD archive" />)}
        {currentTab === 'blends' && (recombinations.length > 0 ? <BlendsTab /> : <StreamingPlaceholder label="recombining" />)}
        {currentTab === 'lenses' && (dmadResults.length > 0 ? <LensesTab /> : <StreamingPlaceholder label="reasoners thinking" />)}
        {currentTab === 'evolve' && (evolveGenCount > 0 ? <EvolutionTimeline /> : <StreamingPlaceholder label="evolving" />)}
        {currentTab === 'competition' && (competitionCount > 0 ? <CompetitionTab /> : <StreamingPlaceholder label="checking the live web" />)}
      </div>
    </div>
  );
}

// ── Streaming placeholder ────────────────────────────────────
// Rendered in a tab whose stage is currently active but hasn't
// produced any rows yet. Avoids the "blank panel" feeling and
// signals progress so users know the engine is working.
function StreamingPlaceholder({ label }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: 16,
        fontSize: 11,
        color: 'var(--text-muted, #777)',
        fontFamily: 'var(--font-mono)',
        background: 'var(--bg-secondary, #0e0e16)',
        border: '1px dashed var(--border, #1f1f1f)',
        borderRadius: 6,
        textAlign: 'center',
      }}
    >
      <span className="refine-pulse" style={{ color: '#a78bfa', marginRight: 6 }}>●</span>
      {label}…
    </div>
  );
}

// ── Per-reasoner live status ────────────────────────────────
// Renders one row per in-flight DMAD reasoner with model name, elapsed
// timer (updates every second), and timeout countdown. Done reasoners
// show ✓ + final ms; errored ones show ✗ + reason. Replaces the silent
// "🧠 reasoners 6/6" bar — gives you the actual story while waiting.
function ReasonerStatusRows({ statusMap }) {
  // Local clock that ticks every second so elapsedMs renders live.
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const anyRunning = Object.values(statusMap || {}).some(s => s.status === 'running');
    if (!anyRunning) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [statusMap]);
  const entries = Object.entries(statusMap || {});
  if (!entries.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4, paddingTop: 4, borderTop: '1px dashed #1a1a2a' }}>
      {entries.map(([id, s]) => {
        const isRunning = s.status === 'running';
        const isDone = s.status === 'done';
        const isError = s.status === 'error';
        // For running reasoners use wall-clock elapsed; for done use final ms.
        const elapsedMs = isRunning && s.startedAt ? (now - s.startedAt) : s.elapsedMs || 0;
        const elapsedSec = Math.round(elapsedMs / 1000);
        const timeoutSec = Math.round((s.timeoutMs || 0) / 1000);
        const pct = isRunning && timeoutSec > 0 ? Math.min(100, (elapsedSec / timeoutSec) * 100) : (isDone ? 100 : 0);
        const color = isDone ? '#34d399' : isError ? '#f87171' : '#a78bfa';
        const icon = isDone ? '✓' : isError ? '✗' : <span className="refine-pulse">●</span>;
        return (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontFamily: 'var(--font-mono)' }}>
            <span style={{ color, minWidth: 14, textAlign: 'center' }}>{icon}</span>
            <span style={{ color: '#bbb', minWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.label}>
              {s.label}
            </span>
            <span style={{ color: '#666', fontSize: 9, minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.model}>
              {(s.model || '').replace(/^(ollama|claude|gemini|deepseek):/, '')}
            </span>
            <div style={{ flex: 1, height: 2, background: '#1a1a1a', borderRadius: 1, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.5s linear' }} />
            </div>
            <span style={{ color: isError ? '#f87171' : '#888', minWidth: 70, textAlign: 'right' }}>
              {isError ? `✗ ${(s.error || 'failed').slice(0, 20)}` : isRunning ? `${elapsedSec}s / ${timeoutSec}s` : `${elapsedSec}s`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Live progress strip ─────────────────────────────────────
// Shows pulsing indicators for each in-flight engine substep. Auto-hides
// when no stage is active. Each row shows X/Y count + a thin progress bar.
function ProgressStrip({ progress, dmadStatus }) {
  if (!progress) return null;
  const { dmadDone, dmadTotal, scoreDone, scoreTotal, tagDone, tagTotal, blendDone, blendTotal, blendScoreDone, blendScoreTotal, activeStage } = progress;
  const rows = [
    { id: 'dmad',        label: '🧠 reasoners',    done: dmadDone,        total: dmadTotal,        active: activeStage === 'dmad' },
    { id: 'score',       label: '📊 scoring nodes', done: scoreDone,       total: scoreTotal,       active: activeStage === 'score' },
    { id: 'tag',         label: '🗺 tagging cells', done: tagDone,         total: tagTotal,         active: activeStage === 'tag' },
    { id: 'blend',       label: '🧬 recombining',  done: blendDone,       total: blendTotal,       active: activeStage === 'blend' },
    { id: 'blend_score', label: '✨ scoring blends', done: blendScoreDone, total: blendScoreTotal, active: activeStage === 'blend_score' },
  ].filter(r => r.total > 0 || r.active);
  const hasReasonerStatus = dmadStatus && Object.keys(dmadStatus).length > 0;
  if (rows.length === 0 && !hasReasonerStatus) return null;
  return (
    <div style={{
      background: '#0e0e16',
      border: '1px solid #1a1a2a',
      borderRadius: 6,
      padding: '8px 10px',
      marginBottom: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }}>
      <div style={{ fontSize: 9, color: '#666', letterSpacing: 1, marginBottom: 2 }}>LIVE PROGRESS</div>
      {rows.map(r => {
        const pct = r.total > 0 ? (r.done / r.total) * 100 : 0;
        const complete = r.total > 0 && r.done >= r.total;
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontFamily: 'var(--font-mono)' }}>
            <span style={{ color: complete ? '#34d399' : r.active ? '#a78bfa' : '#666', minWidth: 130 }}>
              {complete ? '✓' : r.active ? <span className="refine-pulse">●</span> : '○'} {r.label}
            </span>
            <div style={{ flex: 1, height: 3, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: complete ? '#34d399' : '#a78bfa',
                transition: 'width 0.3s ease',
              }} />
            </div>
            <span style={{ color: '#888', minWidth: 40, textAlign: 'right' }}>{r.done}/{r.total || '?'}</span>
          </div>
        );
      })}
      <ReasonerStatusRows statusMap={dmadStatus} />
    </div>
  );
}

// ── Scores tab ──────────────────────────────────────────────
function ScoresTab({ noveltySummary }) {
  const { scores, setFilter, filters, onDrillNovelty, researchStatus } = useNovelty();
  const entries = Object.entries(scores).filter(([, v]) => typeof v.score === 'number');
  const avg = noveltySummary?.avgNovelty
    ?? (entries.length ? Math.round(entries.reduce((s, [, v]) => s + v.score, 0) / entries.length) : null);
  const top = entries.sort((a, b) => b[1].score - a[1].score).slice(0, 5);
  const buckets = { high: 0, mid: 0, low: 0 };
  entries.forEach(([, v]) => {
    if (v.score >= 70) buckets.high++;
    else if (v.score >= 40) buckets.mid++;
    else buckets.low++;
  });
  return (
    <div>
      {/* Research grounding badge — surfaces whether the engine ran on real
          research or training-data opinion. See novelty-engine-phase-a.feature. */}
      <ResearchStatusBadge status={researchStatus} />

      {/* Average + distribution */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
        {avg != null && (
          <div>
            <div style={{ fontSize: 10, color: '#777' }}>AVERAGE</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: noveltyColor(avg) }}>
              {avg} <span style={{ fontSize: 10, color: '#666' }}>· {noveltyLabel(avg)}</span>
            </div>
          </div>
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DistroBar label="70+ notable" count={buckets.high} total={entries.length} color="#34d399" onClick={() => setFilter('minNovelty', filters.minNovelty === 70 ? 0 : 70)} active={filters.minNovelty === 70} />
          <DistroBar label="40-69 variant" count={buckets.mid} total={entries.length} color="#fbbf24" onClick={() => setFilter('minNovelty', filters.minNovelty === 40 ? 0 : 40)} active={filters.minNovelty === 40} />
          <DistroBar label="<40 derivative" count={buckets.low} total={entries.length} color="#f87171" />
        </div>
      </div>

      {/* Top 5 */}
      <div style={{ fontSize: 10, color: '#777', marginBottom: 6 }}>Top 5 most novel</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {top.map(([nid, v]) => (
          <div key={nid} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12 }}>
            <span style={{ color: noveltyColor(v.score), fontWeight: 800, minWidth: 28 }}>{v.score}</span>
            <span style={{ color: '#bbb', flex: 1 }}>
              vs <em style={{ color: '#888' }}>{v.closestExisting || 'unknown'}</em>
              {v.whyNovel && <span style={{ color: '#aaa' }}> — {v.whyNovel}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DistroBar({ label, count, total, color, onClick, active }) {
  const pct = total > 0 ? (count / total * 100) : 0;
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        background: active ? '#2a1f4a' : 'transparent',
        border: active ? '1px solid #6c63ff' : '1px solid transparent',
        padding: '2px 4px',
        borderRadius: 4,
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        color: '#888',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        textAlign: 'left',
      }}
    >
      <span style={{ minWidth: 90 }}>{label}</span>
      <div style={{ flex: 1, height: 4, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, opacity: active ? 1 : 0.6 }} />
      </div>
      <span style={{ minWidth: 24, textAlign: 'right', color: '#aaa' }}>{count}</span>
    </button>
  );
}

// ── Map tab ──────────────────────────────────────────────────
function MapTab() {
  const { archive, filters, setFilter, clearFilters } = useNovelty();
  if (!archive) return <div style={{ fontSize: 11, color: '#666' }}>No QD archive yet.</div>;
  const { dimensions, cells, stats } = archive;
  const mechBins = dimensions?.mechanism || [];
  const riskBins = dimensions?.risk || [];
  // Build mechanism × risk grid (best-in-cell across audience+horizon)
  const grid = {};
  Object.values(cells).forEach(c => {
    const k = `${c.tag.mechanism}|${c.tag.risk}`;
    if (!grid[k] || c.quality > grid[k].quality) grid[k] = c;
  });
  return (
    <div>
      <div style={{ fontSize: 10, color: '#777', marginBottom: 6 }}>
        {stats.filledCells}/{stats.totalCells} cells filled · {Math.round(stats.coverage * 100)}% coverage
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ padding: 2, textAlign: 'left', color: '#666', fontWeight: 500 }}></th>
              {riskBins.map(r => (
                <th key={r} style={{ padding: 4, color: filters.cellRisk === r ? '#fff' : '#888', borderBottom: '1px solid #333', cursor: 'pointer', fontWeight: 500 }} onClick={() => setFilter('cellRisk', filters.cellRisk === r ? null : r)}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mechBins.map(m => (
              <tr key={m}>
                <td style={{ padding: 4, color: filters.cellMechanism === m ? '#fff' : '#888', borderRight: '1px solid #333', whiteSpace: 'nowrap', cursor: 'pointer', fontWeight: 500 }} onClick={() => setFilter('cellMechanism', filters.cellMechanism === m ? null : m)}>{m}</td>
                {riskBins.map(r => {
                  const cell = grid[`${m}|${r}`];
                  return (
                    <td key={r} style={{ padding: 4, verticalAlign: 'top', border: '1px solid #1f1f1f', background: cell ? '#161616' : '#0a0a0a', cursor: cell ? 'pointer' : 'default', minWidth: 80 }}
                      onClick={() => { if (cell) { setFilter('cellMechanism', m); setFilter('cellRisk', r); } }}
                      title={cell ? `${cell.label} · novelty ${cell.novelty?.score ?? '?'}` : 'Empty cell'}
                    >
                      {cell ? (
                        <div>
                          <span style={{ color: noveltyColor(cell.novelty?.score), fontWeight: 700 }}>{cell.novelty?.score ?? '—'}</span>
                          <div style={{ color: '#bbb', fontSize: 10, marginTop: 2, lineHeight: 1.2 }}>{(cell.label || '').slice(0, 38)}</div>
                        </div>
                      ) : <span style={{ color: '#333' }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: '#666', marginTop: 6 }}>
        Click a cell to filter the tree · click a row/col label to filter by that dimension
        {(filters.cellMechanism || filters.cellRisk) && (
          <button onClick={() => clearFilters()} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', marginLeft: 8, fontSize: 10 }}>clear ✕</button>
        )}
      </div>
    </div>
  );
}

// ── Blends tab ───────────────────────────────────────────────
function BlendsTab() {
  const { recombinations, blendScores, onDrillNovelty } = useNovelty();
  const [openId, setOpenId] = useState(null);
  // Sort by novelty score desc
  const ranked = recombinations.map((r, i) => ({ ...r, _id: `blend-${i}`, _score: blendScores[`blend-${i}`]?.score }))
    .sort((a, b) => (b._score ?? 0) - (a._score ?? 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {ranked.map((r) => {
        const isOpen = openId === r._id;
        const hasErr = !!r.error;
        return (
          <div key={r._id} style={{ background: '#181818', borderRadius: 4, padding: '8px 10px' }}>
            <button
              onClick={() => setOpenId(isOpen ? null : r._id)}
              style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 12, padding: 0, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ color: hasErr ? '#f87171' : '#a78bfa' }}>{isOpen ? '▾' : '▸'}</span>
              {!hasErr && r._score != null && (
                <span style={{ color: noveltyColor(r._score), fontWeight: 800, minWidth: 28, fontSize: 13 }}>{r._score}</span>
              )}
              <span style={{ fontWeight: 600, flex: 1 }}>{r.label || r.parents?.map(p => p.label).join(' × ')}</span>
              <span style={{ fontSize: 10, color: '#666' }}>{Math.round((r.ms||0)/100)/10}s</span>
            </button>
            {isOpen && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#bbb', paddingLeft: 16 }}>
                {hasErr ? (
                  <div style={{ color: '#f87171' }}>Error: {r.error}</div>
                ) : (
                  <>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 6, fontStyle: 'italic' }}>
                      Strategy: {r.blendStrategy}
                    </div>
                    <div style={{ color: '#ddd', marginBottom: 8 }}>{r.reasoning}</div>
                    <div style={{ fontSize: 11, paddingLeft: 8, borderLeft: '2px solid #444' }}>
                      <div style={{ color: '#888' }}><strong>From A:</strong> {r.inheritsFrom?.a}</div>
                      <div style={{ color: '#888' }}><strong>From B:</strong> {r.inheritsFrom?.b}</div>
                    </div>
                    {r.parents && (
                      <div style={{ marginTop: 8, fontSize: 10, color: '#555' }}>
                        parents: {r.parents.map(p => `"${(p.label||'').slice(0,30)}"`).join(' × ')}
                      </div>
                    )}
                    {/* Recursive novelty drill */}
                    {onDrillNovelty && (
                      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed #2a2a2a' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDrillNovelty(r.label, r.reasoning, 'blend'); }}
                          style={{
                            background: '#2a1f4a',
                            color: '#fff',
                            border: '1px solid #6c63ff',
                            padding: '6px 12px',
                            fontSize: 11,
                            fontFamily: 'var(--font-mono)',
                            fontWeight: 600,
                            borderRadius: 4,
                            cursor: 'pointer',
                          }}
                          title="Run the full novelty engine on this blend — find blends-of-blends"
                        >
                          🔬 drill into this blend
                        </button>
                        <span style={{ marginLeft: 8, fontSize: 10, color: '#666' }}>
                          re-runs Generate + DMAD + recombine using this as the seed
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Lenses tab (DMAD reasoners) ──────────────────────────────
function LensesTab() {
  const { dmadResults, onAcceptTransformation } = useNovelty();
  const [openId, setOpenId] = useState(null);
  const [acceptedKeys, setAcceptedKeys] = useState({}); // remember which transformations have been added
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {dmadResults.map(r => {
        const isOpen = openId === r.id;
        const hasErr = !!r.error;
        return (
          <div key={r.id} style={{ background: '#181818', borderRadius: 4, padding: '8px 10px' }}>
            <button
              onClick={() => setOpenId(isOpen ? null : r.id)}
              style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 12, padding: 0, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ color: hasErr ? '#f87171' : '#34d399' }}>{isOpen ? '▾' : '▸'}</span>
              <span style={{ fontWeight: 600 }}>{r.label}</span>
              <span style={{ fontSize: 10, color: '#666' }}>{r.method}</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#666' }}>{r.model} · {Math.round((r.ms||0)/100)/10}s</span>
            </button>
            {isOpen && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#bbb', paddingLeft: 16 }}>
                {hasErr ? (
                  <div style={{ color: '#f87171' }}>Error: {r.error}</div>
                ) : (
                  <>
                    {r.output?.critique && (
                      <div style={{ marginBottom: 10, fontStyle: 'italic', color: '#ccc' }}>{r.output.critique}</div>
                    )}
                    {(r.output?.transformations || []).map((t, i) => {
                      const key = `${r.id}-${i}`;
                      const accepted = acceptedKeys[key];
                      return (
                        <div key={i} style={{ marginBottom: 10, paddingLeft: 8, borderLeft: '2px solid #444' }}>
                          <div style={{ color: '#ddd', marginBottom: 2 }}><strong>{t.what}</strong></div>
                          {t.why && <div style={{ color: '#888', fontSize: 11 }}>↳ {t.why}</div>}
                          {t.expectedShift && <div style={{ color: '#34d399', fontSize: 11 }}>→ {t.expectedShift}</div>}
                          {onAcceptTransformation && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAcceptTransformation(t, r.label);
                                setAcceptedKeys(prev => ({ ...prev, [key]: true }));
                              }}
                              disabled={accepted}
                              style={{
                                marginTop: 6,
                                background: accepted ? 'transparent' : '#1a2a1f',
                                color: accepted ? '#34d399' : '#a7f3d0',
                                border: `1px solid ${accepted ? '#34d39940' : '#34d399'}`,
                                padding: '3px 8px',
                                fontSize: 10,
                                fontFamily: 'var(--font-mono)',
                                fontWeight: 600,
                                borderRadius: 4,
                                cursor: accepted ? 'default' : 'pointer',
                              }}
                              title={accepted ? 'Already added to canvas' : 'Add this transformation as a new node in the tree'}
                            >
                              {accepted ? '✓ added to tree' : '+ add to tree'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ── Research status badge ─────────────────────────────────
// Shows up on the SCORES tab to indicate whether the upstream Gemini
// research agents succeeded. Without research, the DMAD reasoners
// (especially Analogical, Counterfactual, Case-Based) fall back to
// training-data knowledge → derivative scores look novel.
function ResearchStatusBadge({ status }) {
  if (!status) return null;
  const { succeededCount, totalCount, briefBytes } = status;
  let icon, color, text;
  if (succeededCount === 0) {
    icon = "⚠️"; color = "var(--error)";
    text = "Research failed (proceeding without grounding)";
  } else if (succeededCount === totalCount) {
    icon = "📚"; color = "var(--success)";
    text = `Research: ${succeededCount}/${totalCount} agents succeeded · ${(briefBytes || 0).toLocaleString()} chars`;
  } else {
    icon = "📚"; color = "var(--warning)";
    text = `Research: ${succeededCount}/${totalCount} agents succeeded · ${(briefBytes || 0).toLocaleString()} chars`;
  }
  return (
    <div
      role="status"
      aria-label={`Upstream research grounding status: ${text}`}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "6px 10px", marginBottom: 12, borderRadius: 4,
        background: "var(--bg-card)", border: `1px solid ${color}`,
        fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-secondary)",
      }}
      title="Phase A reasoners ground their critiques in this research brief. With research, they cite real entities; without, they fall back to training-data knowledge."
    >
      <span aria-hidden="true">{icon}</span>
      <span style={{ color }}>{text}</span>
    </div>
  );
}
